import React, { useState } from 'react';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import InvalidPermissionsDialog from '../invalid-permissions-dialog/InvalidPermissionsDialog';
import { menuItemStyles, styles } from './update-page-header-helper';
import {
    Button,
    Divider,
    IconButton,
    LoadingButton,
    Menu,
    MenuItem,
} from '@biq/apex-ui';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { CancelModal, ConfirmationModal } from 'components';
import { Icon } from '@iconify/react';
import {
    TabItemsType,
    TabsComponent,
} from 'components/landing-page-wrapper/components/TabComponent';
import UploadIcon from '@mui/icons-material/Upload';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';

interface UpdatePageHeaderProps {
    headingText: string;
    deleteMenuItemText: string;
    handleSaveButton: () => void;
    handleCancelButton: () => void;
    handleCancelModal?: () => void;
    handleCancelModalConfirmation?: () => void;
    handleExecuteContract?: () => void;
    isExecuteContractMenuVisible?: boolean;
    isLoading: boolean;
    readOnly?: boolean;
    isChangesUnsaved?: boolean;
    openCancelModal?: boolean;
    unsavedChangesConfirmationText?: string;
    menuWidth?: string;
    openExecuteModal?: boolean;
    handleCloseExecuteModal?: () => void;
    handleExecuteSmartContract?: () => void;
    confirmationModalConfig: {
        confirmationKey?: string;
        confirmationButtonText: string;
        hasConfirmationTextField?: boolean;
        confirmationCancelButtonText?: string;
        confirmationHeadingText: string;
        confirmationInputLabelText?: string;
        confirmationSubHeadingText: React.JSX.Element;
        handleConfirmation: () => void;
        children?: React.ReactNode;
    };
    checkForExternalDeletePermission?: boolean;
    hasDeletePermission?: boolean;
    isTabsComponentVisible?: boolean;
    selectedTab?: any;
    onTabChange?: (event: React.SyntheticEvent, newValue: any) => void;
    tabItems?: TabItemsType[];
    tabsStyles?: any;
    tabItemStyles?: any;
    isMenuComponentVisible?: boolean;
    isEditDictionary?: boolean;
    handleUploadFile?: () => void;
    handleTruncateChain?: () => void;
}

export const UpdatePageHeader = ({
    headingText,
    deleteMenuItemText,
    handleSaveButton,
    handleCancelButton,
    handleCancelModal,
    handleExecuteContract,
    isExecuteContractMenuVisible = false,
    isLoading,
    readOnly,
    isChangesUnsaved,
    openCancelModal,
    unsavedChangesConfirmationText,
    menuWidth,
    openExecuteModal = false,
    handleCloseExecuteModal,
    handleExecuteSmartContract,
    confirmationModalConfig,
    handleCancelModalConfirmation,
    checkForExternalDeletePermission,
    hasDeletePermission,
    isTabsComponentVisible = false,
    selectedTab,
    onTabChange,
    tabItems = [],
    tabsStyles,
    tabItemStyles,
    isMenuComponentVisible = true,
    isEditDictionary = false,
    handleUploadFile,
    handleTruncateChain,
}: UpdatePageHeaderProps): React.JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const handleMenuItem = (): void => {
        if (checkForExternalDeletePermission && hasDeletePermission) {
            setOpenDeleteModal(true);
        } else if (checkForExternalDeletePermission && !hasDeletePermission) {
            setDisplayPermissionDialog(true);
        } else {
            setOpenDeleteModal(true);
        }
        setAnchorEl(null);
    };

    const closeDeleteModal = (): void => {
        setOpenDeleteModal(false);
        setAnchorEl(null);
    };

    const onExecuteMenuItemClick = (): void => {
        setAnchorEl(null);
        handleExecuteContract?.();
    };

    const onUploadFileMenuItemClick = (): void => {
        setAnchorEl(null);
        handleUploadFile?.();
    };

    const onTruncateChainMenuItemClick = (): void => {
        setAnchorEl(null);
        handleTruncateChain?.();
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    return (
        <Grid sx={styles.mainContainer}>
            <Grid sx={styles.headingAndButtonContainer}>
                <Grid>
                    {!isTabsComponentVisible ? (
                        <Typography sx={styles.headingTextStyles}>
                            {headingText}
                        </Typography>
                    ) : (
                        <TabsComponent
                            selectedTab={selectedTab}
                            onTabChange={onTabChange || ((): void => {})}
                            tabItems={tabItems}
                            tabsStyles={tabsStyles}
                            tabItemStyles={tabItemStyles}
                        />
                    )}
                </Grid>
                {!readOnly ? (
                    <Grid sx={styles.buttonContainer}>
                        {isChangesUnsaved ? (
                            <Grid>
                                <Typography
                                    sx={styles.unsavedChangesTextStyles}
                                >
                                    Unsaved changes
                                </Typography>
                            </Grid>
                        ) : null}
                        <Grid>
                            <Button
                                size='small'
                                variant='text'
                                onClick={handleCancelButton}
                                sx={styles.textButtonStyles}
                            >
                                Cancel
                            </Button>
                        </Grid>
                        <Grid>
                            <LoadingButton
                                size='small'
                                onClick={handleSaveButton}
                                disabled={isLoading}
                                color='primary'
                                loading={isLoading}
                                variant='contained'
                                position='end'
                                loadingVariant='withoutIcon'
                                loaderIcon={
                                    <CircularProgress
                                        variant='indeterminate'
                                        disableShrink
                                    />
                                }
                            >
                                Save
                            </LoadingButton>
                        </Grid>
                        {isMenuComponentVisible && (
                            <Grid>
                                <IconButton
                                    aria-label='more'
                                    id='delete-button'
                                    aria-controls={
                                        open ? 'delete-menu' : undefined
                                    }
                                    aria-expanded={open ? 'true' : undefined}
                                    aria-haspopup='true'
                                    onClick={handleClick}
                                    sx={styles.iconButtonStyles}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    id='delete-menu'
                                    aria-labelledby='delete-button'
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'center',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    sx={menuItemStyles(menuWidth ?? '184px')}
                                >
                                    {isExecuteContractMenuVisible ? (
                                        <Grid>
                                            <MenuItem
                                                onClick={onExecuteMenuItemClick}
                                            >
                                                <Box
                                                    sx={
                                                        styles.deleteMenuItemContainer
                                                    }
                                                >
                                                    <Icon
                                                        icon='mdi:file-sign'
                                                        style={
                                                            styles.executeContractIconStyles
                                                        }
                                                    />
                                                    <Typography
                                                        sx={
                                                            styles.deleteTextStyles
                                                        }
                                                    >
                                                        Execute Contract
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                            <Divider />
                                        </Grid>
                                    ) : null}
                                    {isEditDictionary && (
                                        <Grid>
                                            <MenuItem
                                                onClick={
                                                    onUploadFileMenuItemClick
                                                }
                                            >
                                                <Box
                                                    sx={
                                                        styles.deleteMenuItemContainer
                                                    }
                                                >
                                                    <UploadIcon
                                                        sx={
                                                            styles.uploadIconStyles
                                                        }
                                                    />
                                                    <Typography
                                                        sx={
                                                            styles.deleteTextStyles
                                                        }
                                                    >
                                                        Upload File
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                            <Divider
                                                sx={styles.dividerStyles}
                                            />
                                        </Grid>
                                    )}
                                    <MenuItem onClick={handleMenuItem}>
                                        <Box
                                            sx={styles.deleteMenuItemContainer}
                                        >
                                            <DeleteIcon
                                                sx={styles.deleteIconStyles}
                                            />
                                            <Typography
                                                sx={styles.deleteTextStyles}
                                            >
                                                {deleteMenuItemText}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                    {isEditDictionary && (
                                        <MenuItem
                                            onClick={
                                                onTruncateChainMenuItemClick
                                            }
                                            sx={
                                                styles.trunacteChainIconMenuContainerStyles
                                            }
                                        >
                                            <Box
                                                sx={
                                                    styles.deleteMenuItemContainer
                                                }
                                            >
                                                <DisabledByDefaultIcon
                                                    sx={
                                                        styles.trunacteChainIconStyles
                                                    }
                                                />
                                                <Typography
                                                    sx={styles.deleteTextStyles}
                                                >
                                                    Truncate Chain
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    )}
                                </Menu>
                            </Grid>
                        )}
                    </Grid>
                ) : null}
            </Grid>
            {openDeleteModal && (
                <ConfirmationModal
                    confirmationModal={openDeleteModal}
                    handleConfirmationModal={closeDeleteModal}
                    confirmationKey={confirmationModalConfig?.confirmationKey}
                    hasConfirmationTextField={
                        confirmationModalConfig?.hasConfirmationTextField
                    }
                    confirmationButtonText={
                        confirmationModalConfig?.confirmationButtonText
                    }
                    confirmationCancelButtonText={
                        confirmationModalConfig?.confirmationCancelButtonText
                    }
                    confirmationHeadingText={
                        confirmationModalConfig.confirmationHeadingText
                    }
                    confirmationInputLabelText={
                        confirmationModalConfig.confirmationInputLabelText
                    }
                    handleConfirmation={
                        confirmationModalConfig.handleConfirmation
                    }
                    confirmationSubHeadingText={
                        confirmationModalConfig.confirmationSubHeadingText
                    }
                />
            )}
            {openCancelModal && (
                <CancelModal
                    cancelModal={openCancelModal}
                    handleCancelModal={handleCancelModal}
                    cancelButtonText='Close without saving'
                    cancelHeadingText='Are you sure you want to abandon your changes?'
                    cancelSubHeadingText={unsavedChangesConfirmationText}
                    handleCancel={handleCancelModalConfirmation}
                />
            )}
            {openExecuteModal && (
                <CancelModal
                    cancelModal={openExecuteModal}
                    handleCancelModal={handleCloseExecuteModal}
                    cancelButtonText='Save and execute'
                    cancelHeadingText='You have unsaved changes.'
                    cancelSubHeadingText='This smart contract has unsaved changes. You must save changes before you can execute contract.'
                    cancelModalText='Go Back'
                    handleCancel={handleExecuteSmartContract}
                />
            )}
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </Grid>
    );
};
